import imageUrlBuilder from "@sanity/image-url";
import { client } from "./client";
import { dataset, projectId } from "./api";
import { Metadata } from "next";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { getPage } from "@/lib/data";

const builder = imageUrlBuilder(client);

export function urlFor(source: SanityImageSource) {
  if (!source) return "";

  if (_isNotSanityImage(source)) return source;

  try {
    return builder.image(source).url();
  } catch (error) {
    console.error("Error building image URL:", error, "Source:", source);
    return "";
  }
}

/**
 * Cloudflare-wrapped URL for image optimization.
 * Only active in production builds if NEXT_PUBLIC_CF_IMAGE_ZONE is set.
 */
export function cfUrlFor(source: SanityImageSource, options: Record<string, string | number> = {}) {
  if (!source) return "";

  if (typeof source === "string") {
    if (!source.startsWith("image-") && !source.startsWith("file-")) {
      console.warn("Invalid Sanity image reference format:", source);
      return source;
    }
  }

  if (_isNotSanityImage(source)) return source;

  let sanityUrl: string;
  try {
    sanityUrl = builder.image(source).url();
  } catch (error) {
    console.error("Error building Sanity image URL:", error, "Source:", source);
    return "";
  }

  if (process.env.NODE_ENV !== "production" || !process.env.NEXT_PUBLIC_CF_IMAGE_ZONE) {
    return sanityUrl;
  }

  const zone = process.env.NEXT_PUBLIC_CF_IMAGE_ZONE;
  const params = new URLSearchParams(
    Object.entries(options).map(([k, v]) => [k, String(v)])
  ).toString();

  //return `https://${zone}/cdn-cgi/image/${params ? params + "/" : ""}${sanityUrl}`;
  if (params){
    return `https://${zone}/cdn-cgi/image/${params}/${sanityUrl}`;
  }
  return sanityUrl;
}

const _isExternalImage = (source: SanityImageSource): source is string => {
  return typeof source === "string" && /^https?:\/\//.test(source);
};

const _isLocalImage = (source: SanityImageSource): source is string => {
  return typeof source === "string" && source.startsWith("/");
};

const _isNotSanityImage = (source: SanityImageSource): source is string => {
  return _isExternalImage(source) || _isLocalImage(source);
};

export function getFileUrl(ref: string) {
  if (!ref || typeof ref !== "string") return "";

  const parts = ref.split("-");
  if (parts.length < 3) {
    console.error("Invalid file reference format:", ref);
    return "";
  }

  const [, id, extension] = parts;
  return `https://cdn.sanity.io/files/${projectId}/${dataset}/${id}.${extension}`;
}

export async function _generateMetadata(slug: string): Promise<Metadata> {
  const page = await getPage(slug);
  if (!page || !page.metadata) {
    return {
      title: "Page Not Found",
      description: "The page you are looking for does not exist.",
    };
  }
  const metadata = page.metadata;

  // Helper to safely get image URL
  const getImageUrl = (image: any) => {
    if (!image) return undefined;
    try {
      return cfUrlFor(image);
    } catch (error) {
      console.error("Error generating image URL for metadata:", error);
      return undefined;
    }
  };

  const imageUrl = getImageUrl(metadata.image);

  return {
    title: metadata.title || metadata.ogTitle || "",
    description: metadata.description || metadata.ogDescription,
    applicationName: metadata.siteName,
    openGraph: {
      title: metadata.ogTitle || metadata.title,
      description: metadata.ogDescription || metadata.description,
      type: metadata.ogType as "website",
      siteName: metadata.siteName,
      url: metadata.url,
      images: imageUrl
        ? [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: metadata.title,
            },
          ]
        : undefined,
    },
    twitter: {
      card: "summary_large_image",
      title: metadata.ogType || metadata.title,
      description: metadata.ogDescription || metadata.description,
      images: imageUrl
        ? [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: metadata.title,
            },
          ]
        : undefined,
    },
  };
}
